'use client'

import React from 'react'
import Link from 'next/link'
import { Home, ChevronRight } from 'lucide-react'
import { useTheme } from '../theme/provider'

export interface BreadcrumbItem {
  label: string
  href: string
  current?: boolean
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[]
  locale?: string
  /**
   * Base URL for Schema.org structured data
   * @default "https://example.com"
   */
  baseUrl?: string
  /**
   * Show home icon
   * @default true
   */
  showHomeIcon?: boolean
  /**
   * Custom home label (overrides theme translations)
   */
  homeLabel?: string
  /**
   * Custom aria-label (overrides theme translations)
   */
  ariaLabel?: string
}

/**
 * Breadcrumb Component
 * Navigation component with locale support and Schema.org structured data
 *
 * Features:
 * - Theme-customizable colors
 * - Multi-locale support
 * - Schema.org BreadcrumbList structured data
 * - Accessible navigation
 * - Hover effects
 *
 * @example
 * ```tsx
 * <Breadcrumb
 *   items={[
 *     { label: 'News', href: '/news' },
 *     { label: 'Article Title', href: '/news/article-123', current: true }
 *   ]}
 *   locale="en"
 *   baseUrl="https://yoursite.com"
 * />
 * ```
 */
export function Breadcrumb({
  items,
  locale = 'en',
  baseUrl = 'https://example.com',
  showHomeIcon = true,
  homeLabel,
  ariaLabel,
}: BreadcrumbProps) {
  const { theme, translations } = useTheme()

  const primaryColor = theme.colors.primary
  const homeLabelText = homeLabel || translations.breadcrumb?.home || 'Home'
  const ariaLabelText = ariaLabel || translations.breadcrumb?.ariaLabel || 'Breadcrumb'

  // Helper function to generate localized paths
  const getLocalizedPath = (path: string) => {
    // For default locale, return path as-is
    if (locale === 'en' || locale === 'fr') return path
    // For other languages, add language prefix if needed
    if (!path.startsWith(`/${locale}`)) {
      return `/${locale}${path}`
    }
    return path
  }

  // Generate Schema.org BreadcrumbList structured data
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.current
        ? undefined
        : `${baseUrl}${getLocalizedPath(item.href)}`,
    })),
  }

  return (
    <>
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      {/* Breadcrumb Navigation */}
      <nav
        aria-label={ariaLabelText}
        className="flex items-center gap-2 text-sm mb-6 px-5 py-3.5 rounded-xl border backdrop-blur-sm shadow-sm"
        style={{
          backgroundColor:
            theme.components?.breadcrumb?.background ||
            theme.colors.surface,
          borderColor:
            theme.components?.breadcrumb?.borderColor ||
            theme.colors.border,
        }}
      >
        <ol className="flex items-center gap-2.5 flex-wrap">
          {/* Home Icon */}
          <li className="flex items-center">
            <Link
              href={getLocalizedPath('/')}
              className="flex items-center gap-2 hover:text-current transition-all duration-300 group relative"
              style={{
                color: theme.colors.textSecondary,
                ['--hover-color' as any]: primaryColor,
              }}
              aria-label={homeLabelText}
            >
              {showHomeIcon && (
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                  style={{
                    backgroundColor: `${theme.colors.background}`,
                  }}
                >
                  <Home className="w-4 h-4" />
                </div>
              )}
              <span className="font-semibold text-xs uppercase tracking-wide">
                <span
                  className="group-hover:[color:var(--hover-color)] transition-colors duration-300"
                  style={{
                    ['--hover-color' as any]: primaryColor,
                  }}
                >
                  {homeLabelText}
                </span>
              </span>
              {/* Hover underline */}
              <span
                className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                style={{ backgroundColor: primaryColor }}
              />
            </Link>
          </li>

          {/* Breadcrumb Items */}
          {items.map((item, index) => (
            <li key={index} className="flex items-center gap-2.5">
              {/* Separator */}
              <div
                className="w-1 h-1 rounded-full"
                style={{ backgroundColor: theme.colors.textSecondary }}
                aria-hidden="true"
              />

              {item.current ? (
                <span
                  className="font-bold text-xs uppercase tracking-wide px-3 py-1.5 rounded-lg"
                  style={{
                    color: '#ffffff',
                    backgroundColor: primaryColor,
                  }}
                  aria-current="page"
                >
                  {item.label}
                </span>
              ) : (
                <Link
                  href={getLocalizedPath(item.href)}
                  className="hover:text-current transition-all duration-300 font-semibold text-xs uppercase tracking-wide group relative px-3 py-1.5 rounded-lg hover:bg-current/5"
                  style={{
                    color: theme.colors.textSecondary,
                    ['--hover-color' as any]: primaryColor,
                  }}
                >
                  <span
                    className="group-hover:[color:var(--hover-color)] transition-colors duration-300"
                    style={{
                      ['--hover-color' as any]: primaryColor,
                    }}
                  >
                    {item.label}
                  </span>
                </Link>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  )
}
